import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { BtPttState } from '../store';

/**
 * Bridge to the native shells (packages/mobile). On the web everything here is a no-op.
 *
 * AudioSession (iOS): sets AVAudioSession to playAndRecord/voiceChat so WebRTC audio uses the
 * loudspeaker (or headset/Bluetooth when connected) and keeps running with the screen locked.
 */
export interface AudioSessionPlugin {
  configure(opts: { speaker: boolean }): Promise<{ route: string }>;
  insets(): Promise<{ top: number; bottom: number; left: number; right: number }>;
  keepAlive(opts: { on: boolean }): Promise<void>;
}

const AudioSession = registerPlugin<AudioSessionPlugin>('AudioSession');

/** Bluetooth LE push-to-talk button (packages/mobile: PttButtonPlugin.swift / .java). */
export interface PttButtonPlugin {
  start(opts: { deviceId?: string }): Promise<void>;
  stop(): Promise<void>;
  addListener(event: 'state', fn: (e: { state: BtPttState; id?: string; name?: string }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'ptt', fn: (e: { pressed: boolean }) => void): Promise<PluginListenerHandle>;
}

export const PttButton = registerPlugin<PttButtonPlugin>('PttButton');

export const isNative = (): boolean => Capacitor.isNativePlatform();

/** Bridge exposed by the BCComms desktop app's preload (packages/desktop-client). */
export interface DesktopBridge {
  platform: 'darwin' | 'win32' | 'linux';
  /** Capture devices with their channel counts (Dante Virtual Soundcard / Dante Via show up here). */
  listInterfaces(): Promise<{ name: string; channels: number; error?: string }[]>;
  /** Start streaming one channel (1-based) of `device` as 48 kHz mono float PCM. */
  startCapture(device: string, channel: number): Promise<void>;
  stopCapture(): Promise<void>;
  onPcm(fn: (samples: Float32Array) => void): () => void;
  onCaptureError(fn: (message: string) => void): () => void;
}

export const desktop = (): DesktopBridge | null => (window as Window & { bccommsDesktop?: DesktopBridge }).bccommsDesktop ?? null;
export const isDesktop = (): boolean => desktop() !== null;
/** Apps (mobile or desktop) are not served by the server, so they ask for its address at login. */
export const needsServerAddress = (): boolean => isNative() || isDesktop();
export const nativePlatform = (): 'ios' | 'android' | 'web' => Capacitor.getPlatform() as 'ios' | 'android' | 'web';

export async function configureNativeAudio(speaker: boolean): Promise<string | null> {
  if (nativePlatform() !== 'ios') return null;
  try {
    const r = await AudioSession.configure({ speaker });
    return r.route;
  } catch (err) {
    console.warn('AudioSession.configure failed', err);
    return null;
  }
}

/**
 * Android: run a foreground service (ongoing notification + wake/Wi-Fi locks) while connected, so
 * intercom audio and the mic keep working with the screen locked or the app in the background.
 * iOS gets the same from UIBackgroundModes: audio, so this is Android-only.
 */
export async function keepNativeAlive(on: boolean): Promise<void> {
  if (nativePlatform() !== 'android') return;
  try {
    await AudioSession.keepAlive({ on });
  } catch (err) {
    console.warn('AudioSession.keepAlive failed', err);
  }
}

// ---- server address -----------------------------------------------------------------------------
// Browser builds are served by the server itself (same origin). Native apps must be told where the
// server lives; the choice is remembered on the device.

const SERVER_KEY = 'bccomms.server';

export function defaultServer(): string {
  if (!needsServerAddress()) return location.origin;
  return localStorage.getItem(SERVER_KEY) ?? '';
}

export function setServer(url: string): string {
  const clean = normalizeServer(url);
  localStorage.setItem(SERVER_KEY, clean);
  return clean;
}

/**
 * Accepts "comms.example.com", "192.168.1.20:4000", "https://…". Without a scheme, domain names get
 * https (hosted servers sit behind TLS, and an http WebSocket can't follow the redirect), while LAN
 * addresses (IPs, localhost, .local, or an explicit port) get http.
 */
export function normalizeServer(input: string): string {
  let s = input.trim().replace(/\/+$/, '');
  if (!s) return '';
  if (!/^https?:\/\//i.test(s)) {
    const host = s.split('/')[0];
    const lan = /:\d+$/.test(host) || /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || /^localhost$/i.test(host) || /\.local$/i.test(host) || host.startsWith('[');
    s = `${lan ? 'http' : 'https'}://${s}`;
  }
  return s;
}

/** Base URL for HTTP API calls ('' = same origin). */
export function apiBase(): string {
  if (!needsServerAddress()) return '';
  return defaultServer();
}

export function wsUrlFor(server: string): string {
  const base = server || location.origin;
  return `${base.replace(/^http/i, 'ws')}/ws`;
}

/** iOS: publish native safe-area insets as CSS variables (--sat / --sab); web falls back to env(). */
export async function applyNativeInsets(): Promise<void> {
  if (nativePlatform() !== 'ios') return;
  try {
    const i = await AudioSession.insets();
    const root = document.documentElement.style;
    // Only publish real values: a 0 here would mask the env() fallback in CSS.
    if (i.top > 0) root.setProperty('--sat', `${Math.round(i.top)}px`);
    if (i.bottom > 0) root.setProperty('--sab', `${Math.round(i.bottom)}px`);
  } catch (err) {
    console.warn('insets failed', err);
  }
}

/** Insets are 0 until the first native layout pass, so poll briefly after launch. */
export function watchNativeInsets(): void {
  if (!isNative()) return;
  for (const ms of [0, 300, 1000, 3000]) window.setTimeout(() => void applyNativeInsets(), ms);
  window.addEventListener('resize', () => void applyNativeInsets());
  window.addEventListener('orientationchange', () => window.setTimeout(() => void applyNativeInsets(), 300));
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void applyNativeInsets());
}



